<template>
  <div
    class="step-card text-gray03"
    @keydown.alt.50="nextPage()"
    @keydown.alt.49="previousPage()"
  >
    <div class="card card-survey-steps">
      <div class="card-answer-wrapper card-answer-ie-11">
        <Answer
          v-if="currentQuestion && !isFeedback && isAnswer"
          :question="currentQuestion"
          :page="surveyPage"
          :key="currentQuestion.question_id"
          @answer="selectAnswer"
        />

        <Feedback
          v-if="isFeedback"
          :currentAnswered="currentAnswered"
          @selectFeedback="selectFeedback"
        />
      </div>

      <div class="card-footer text-muted">
        <b-button
          id="btn-previous"
          @click="previousPage"
          :aria-label="`${resources.component.steps.previous}, Alt + 1`"
          class="btn-previous mdi mdi-arrow-left"
          variant="light"
          :tabindex="currentQuestionAnswersCount + 4"
          :disabled="surveyPage.loading || surveyPage.current === 1"
          :aria-hidden="surveyPage.loading || surveyPage.current === 1"
        >
          <span>
            {{ resources.component.steps.previous }}
          </span>
        </b-button>

        <div class="flex-grow" aria-hidden="true">
          <PageIndicators class="page-indicator" :page="surveyPage" />
        </div>

        <b-button
          id="btn-next"
          @click="nextPage()"
          :aria-label="`${labelButtonNext}, Alt + 2`"
          :tabindex="currentQuestionAnswersCount + 5"
          :class="{ 'mdi mdi-loading mdi-spin': surveyPage.loading }"
          variant="light"
          :disabled="surveyPage.loading"
        >
          <span>{{ labelButtonNext }}</span>
          <i class="mdi mdi-arrow-right"></i>
        </b-button>
      </div>
    </div>
    <b-modal
      id="modal-error"
      :title="resources.page.survey.errorMessage.title"
      cancel-variant="outline-primary"
      ref="modal-error"
      header-bg-variant="danger"
    >
      <p class="txt-20">
        {{ resources.page.survey.errorMessage.message1 }}
      </p>
      <p class="txt-20">
        {{ resources.page.survey.errorMessage.message2 }}
      </p>
      <template v-slot:modal-footer>
        &nbsp;
      </template>
    </b-modal>
  </div>
</template>

<script>
import { mapGetters } from 'vuex'
import Answer from '../../components/Survey/Questions/Answer.vue'
import PageIndicators from '../../components/Survey/PageIndicators.vue'
import Feedback from '../../components/Survey/Questions/Feedback.vue'

export default {
  components: {
    Answer,
    PageIndicators,
    Feedback,
  },

  data() {
    return {
      isFeedback: false,
      isAnswer: true,
      currentAnsweredTemp: {
        feedback_answer: undefined,
      },
    }
  },

  computed: {
    ...mapGetters({
      customSurvey: 'survey/customSurvey',
      survey: 'survey/survey',
      currentQuestion: 'survey/currentQuestion',
      currentAnswered: 'survey/currentAnswered',
      surveyPage: 'survey/surveyPage',
      resources: 'resources',
    }),

    labelButtonNext: function() {
      let isAnswered = Object.keys(this.currentAnswered).length !== 0

      const isFeedback = this.currentAnswered && this.currentAnswered.feedback
      const isFeedbackAnswered =
        this.currentAnswered && this.currentAnswered.feedback_answer

      if (isFeedback && !this.isAnswer) isAnswered = isFeedbackAnswered

      return isAnswered
        ? this.resources.component.steps.next
        : this.resources.component.steps.skip
    },

    currentQuestionAnswersCount() {
      return this.isFeedback ||
        (this.currentQuestion && this.currentQuestion.type === 'percent')
        ? 1
        : this.currentQuestion
        ? this.currentQuestion.answers.length
        : 0
    },
  },

  // Load card images before open steps
  beforeRouteEnter(to, from, next) {
    if (!localStorage || !localStorage.survey) {
      next()
      return false
    }
    const survey = JSON.parse(localStorage.survey)
    const images = [].concat(
      ...survey
        .filter(s => s.type === 'radio-card' || s.type === 'percent')
        .map(s => s.answers.map(a => a.image))
    )
    if (!images || images.length === 0) {
      next()
    }
    images.forEach(i => {
      let image = new Image()
      image.src = i
    })

    next()
  },

  methods: {
    nextPage() {
      if (
        !this.isFeedback &&
        this.currentAnswered &&
        this.currentAnswered.feedback &&
        !this.$can('use', 'libras')
      ) {
        this.isFeedback = true
        this.isAnswer = false
        return
      }

      if (this.surveyPage.total !== this.surveyPage.current) {
        this.isFeedback = false
        this.$store.dispatch('survey/surveyNextPage')
        this.$store.commit('survey/sendSurveyToLocalStorage')
      } else {
        this.finishSurvey()
      }
    },
    selectAnswer(answer) {
      if (answer) {
        try {
          if (this.currentQuestion.answers[0].feedback) {
            if (this.currentAnswered.feedback_answer) {
              answer.feedback_answer = Object.assign(
                {},
                JSON.parse(JSON.stringify(this.currentAnswered.feedback_answer))
              )
            } else {
              answer.feedback_answer = null
            }
          }
        } catch (e) {
          answer.feedback_answer = null
        }

        const selectAnswer = JSON.parse(JSON.stringify(answer))
        this.$store.commit('survey/selectAnswer', selectAnswer)
      }
    },
    selectFeedback(feedback) {
      this.$store.commit('survey/selectAnswerFeedback', feedback)
    },
    previousPage() {
      if (this.surveyPage.current === 1) return
      if (this.isFeedback) {
        this.isFeedback = false
        this.isAnswer = true
      } else {
        this.$store.dispatch('survey/surveyPreviousPage')
      }
      this.$store.commit('survey/sendSurveyToLocalStorage')
    },
    async finishSurvey() {
      this.$store.commit('survey/toggleSurveyPostLoading')

      const customSurvey = this.customSurvey

      const requests = [
        this.$store.dispatch('survey/finishSurvey').catch(() => {
          this.$refs['modal-error'].show()
          this.$store.commit('survey/toggleSurveyPostLoading')
        }),
      ]

      if (customSurvey) {
        requests.push(
          this.$store.dispatch('customSurvey/getSurvey', customSurvey)
        )
      }

      await Promise.all(requests)

      if (customSurvey) {
        return this.$router.push({
          name: 'custom-surveys.survey.steps',
          params: { id: customSurvey },
          query: { origin: 'engagement-survey' },
        })
      }

      return this.$router.push('conclusion')
    },
  },
}
</script>

<style scoped lang="scss">
@import '~@/styles/custom/colors';

_:-ms-fullscreen,
:root .card-answer-ie-11 {
  display: block !important;
}

.step-card {
  background-image: linear-gradient(
    to bottom,
    rgba(0, 0, 255, 0) 20%,
    map-get($theme-colors, 'light') 20%
  );
  bottom: 0;
  display: flex;
  justify-content: center;
  left: 0;
  min-height: 100%;
  overflow: auto;
  padding: 6rem 0;
  position: absolute;
  right: 0;
  top: 0;

  @media (max-height: 800px) {
    padding: 2rem 0;
  }

  @media (max-width: 425px) {
    padding: 0;
  }

  .card-survey-steps {
    display: flex;
    flex-direction: column;
    max-width: 80%;
    text-align: center;
    width: 100%;

    @media (max-width: 768px) {
      max-width: 95%;
    }

    @media (max-width: 425px) {
      border-radius: 0;
      max-width: initial;
    }

    .card-answer-wrapper {
      display: flex;
      flex-grow: 1;
      justify-content: center;
      overflow: auto;
      padding: 3rem 1rem;

      @media (max-width: 768px) {
        padding: 1.5rem;
        text-align: center;
      }

      @media (max-width: 425px) {
        padding: 1rem;
      }

      @media (max-height: 520px) {
        padding: 0.1rem 1rem;
      }
    }

    .card-footer {
      align-items: center;
      background-color: map-get($theme-colors, 'gray06');
      display: flex;
      min-height: 70px;
      width: 100%;

      @media (max-width: 768px) {
        border-radius: 0;
      }

      @media (max-width: 425px) {
        min-height: 70px;
      }

      .flex-grow {
        flex-grow: 1;
      }

      .btn {
        align-items: center;
        background: none;
        border: 1px solid map-get($theme-colors, 'gray-dark');
        color: map-get($theme-colors, 'gray03');
        display: flex;
        justify-content: space-between;
        padding: 0px 15px;
        width: 230px;

        &:not(:disabled):active {
          background-color: map-get($theme-colors, 'red');
          border: 1px solid map-get($theme-colors, 'red');
          color: map-get($theme-colors, 'white');
        }

        &.btn-previous:disabled {
          opacity: 0;
        }

        @media (max-width: 900px) {
          font-size: 22px;
          justify-content: center;
          padding: 5px 16px;
          width: 50px;
          height: 50px !important;
        }

        @media (max-width: 320px) {
          font-size: 15px;
          width: 18px;
          height: 34px !important;
        }

        span {
          font-size: 17px;

          @media (max-width: 900px) {
            display: none;
          }
        }
      }
    }
  }
}
</style>
